import { ReactNode } from "react";
import { Badge } from "@tellescope/ui";
import { i18n } from "@tellescope/i18n";
import { isValidDate } from "@tellescope/utils";
import { TextCell } from "../TextCell";
import { EnduserCellWrapper, EnduserRecord } from "./EnduserCellWrapper";

export interface EnduserFieldCellProps {
  enduserId?: string;
  /** A field key string to look up on the enduser, or a callback that extracts the value to render */
  fieldSelector: string | ((enduser: EnduserRecord) => unknown);
  /** Optional override for rendering — receives the resolved value and the enduser record */
  customRenderFn?: (value: unknown, enduser: EnduserRecord) => ReactNode;
}

/**
 * Renders the value as a date if it's a valid timestamp or date string.
 * Returns null if not a date.
 */
const tryRenderDate = (value: unknown): ReactNode | null => {
  if (typeof value === "number") {
    const date = new Date(value);
    if (isValidDate(date)) return <DateDisplay date={date} />;
  }
  if (typeof value === "string" && value.includes("-") && !isNaN(Date.parse(value))) {
    const date = new Date(value);
    if (isValidDate(date)) return <DateDisplay date={date} />;
  }
  return null;
};

const DateDisplay = ({ date }: { date: Date }) => (
  <div className="flex flex-col">
    <span className="text-foreground font-medium">{date.toLocaleDateString()}</span>
    <span className="text-muted-foreground text-xs">
      {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
    </span>
  </div>
);

/**
 * Default renderer that infers the right presentation from the value type.
 */
const renderValue = (value: unknown): ReactNode => {
  // Nullish
  if (value === undefined || value === null || value === "") {
    return <TextCell>—</TextCell>;
  }

  // Boolean
  if (typeof value === "boolean") {
    return (
      <Badge variant={value ? "secondary" : "outline"} className="text-xs">
        {value ? i18n.t("Yes", { ns: "common" }) : i18n.t("No", { ns: "common" })}
      </Badge>
    );
  }

  // Array
  if (Array.isArray(value)) {
    if (value.length === 0) return <TextCell>—</TextCell>;
    return (
      <div className="flex gap-1 overflow-x-auto pb-2">
        {value.map((item, idx) => (
          <Badge key={idx} variant="secondary" className="text-xs whitespace-nowrap">
            {typeof item === "object" && item !== null ? JSON.stringify(item) : String(item)}
          </Badge>
        ))}
      </div>
    );
  }

  // Date (number or string)
  const dateNode = tryRenderDate(value);
  if (dateNode) return dateNode;

  // Object (e.g. height/weight with value+unit)
  if (typeof value === "object" && value !== null) {
    const obj = value as Record<string, unknown>;
    if ("value" in obj && "unit" in obj) {
      return <TextCell>{`${obj.value} ${obj.unit}`}</TextCell>;
    }
    return <TextCell>{JSON.stringify(value)}</TextCell>;
  }

  // Fallback: string / number
  return <TextCell>{String(value)}</TextCell>;
};

/**
 * Consolidated cell for displaying any enduser field in a table.
 *
 * - `fieldSelector` can be a field key (string) or a callback `(enduser) => value`
 * - The resolved value is auto-rendered based on its type (text, boolean, array, date, object)
 * - Pass `customRenderFn` to override the default rendering
 */
export const EnduserFieldCell = ({ enduserId, fieldSelector, customRenderFn }: EnduserFieldCellProps) => (
  <EnduserCellWrapper enduserId={enduserId}>
    {(enduser) => {
      const value =
        typeof fieldSelector === "function"
          ? fieldSelector(enduser)
          : (enduser as unknown as Record<string, unknown>)[fieldSelector];

      if (customRenderFn) {
        return <>{customRenderFn(value, enduser)}</>;
      }

      return <>{renderValue(value)}</>;
    }}
  </EnduserCellWrapper>
);
